import React, { useEffect, useState } from "react";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import HUDFrame from "./HUDFrame";
import { submitDemoRequest } from "../lib/api";

const initial = {
  name: "",
  email: "",
  company: "",
  phone: "",
  role: "",
  company_size: "",
  state: "",
  message: "",
};

export default function RequestDemoModal({ open, onClose }) {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setForm(initial);
        setDone(false);
        setError("");
      }, 200);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await submitDemoRequest(form);
      setDone(true);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.[0]?.msg ||
          err?.response?.data?.detail ||
          "Unable to submit. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      data-testid="request-demo-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />
      <HUDFrame className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto" padding="p-7 md:p-10">
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 text-[color:var(--ss-green)] hover:text-white"
          data-testid="modal-close"
        >
          <X size={22} />
        </button>

        {done ? (
          <div className="text-center py-10" data-testid="demo-success">
            <CheckCircle2 className="mx-auto text-[color:var(--ss-green)]" size={64} />
            <h3 className="mt-5 text-2xl md:text-3xl font-display glow">Request received</h3>
            <p className="mt-3 text-[color:var(--ss-muted)] max-w-md mx-auto">
              Thanks — our enterprise team will reach out to{" "}
              <span className="text-[color:var(--ss-green)]">{form.email}</span> within 24 hours.
            </p>
            <button type="button" className="btn-ghost mt-8" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="hud-pill mb-4">REQUEST ACCESS</div>
            <h3 className="text-3xl md:text-4xl font-display font-black">
              Get a <span className="text-green glow">Custom Tailored Demo</span> for your Company
            </h3>
            <p className="mt-2 text-[color:var(--ss-muted)]">
              Tell us about your operation — we’ll schedule a walkthrough of the platform.
            </p>

            <form className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" onSubmit={submit}>
              <div>
                <label className="ss-label">Full name</label>
                <input required className="ss-input" value={form.name} onChange={onChange("name")} data-testid="demo-name" />
              </div>
              <div>
                <label className="ss-label">Work email</label>
                <input required type="email" className="ss-input" value={form.email} onChange={onChange("email")} data-testid="demo-email" />
              </div>
              <div>
                <label className="ss-label">Company</label>
                <input required className="ss-input" value={form.company} onChange={onChange("company")} data-testid="demo-company" />
              </div>
              <div>
                <label className="ss-label">Phone</label>
                <input className="ss-input" value={form.phone} onChange={onChange("phone")} data-testid="demo-phone" />
              </div>
              <div>
                <label className="ss-label">Role</label>
                <input className="ss-input" value={form.role} onChange={onChange("role")} data-testid="demo-role" placeholder="Owner, Ops Manager…" />
              </div>
              <div>
                <label className="ss-label">State / Region</label>
                <input className="ss-input" value={form.state} onChange={onChange("state")} data-testid="demo-state" />
              </div>
              <div className="md:col-span-2">
                <label className="ss-label">Company size</label>
                <select className="ss-select" value={form.company_size} onChange={onChange("company_size")} data-testid="demo-size">
                  <option value="">Select…</option>
                  <option>1 location</option>
                  <option>2–5 locations</option>
                  <option>6–20 locations</option>
                  <option>20+ locations (Enterprise)</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="ss-label">Anything we should know?</label>
                <textarea rows={3} className="ss-textarea" value={form.message} onChange={onChange("message")} data-testid="demo-message" />
              </div>

              {error && (
                <div className="md:col-span-2 text-red-400 text-sm" data-testid="demo-error">
                  {error}
                </div>
              )}

              <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 items-center justify-end pt-2">
                <button type="button" className="btn-ghost" onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading} data-testid="demo-submit">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                  {loading ? "Submitting…" : "Submit Request"}
                </button>
              </div>
            </form>
          </>
        )}
      </HUDFrame>
    </div>
  );
}
